// /jobs — Jobs & DLQ inspector.
//
// Tres secciones:
//   1. Scheduler runs (historial audit_log)
//   2. Cobros frenados por un error tecnico del procesador (migr. 0040), con
//      el boton que libera el freno.
//   3. Queues + DLQs (SQS stats) con redrive button por queue.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchJobsStatus,
  redriveDlq,
  retryChargeErrors,
  AdminApiError,
  type ChargeErrorGroup,
  type QueueInfo,
} from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import ConfirmDialog from '@/components/ConfirmDialog';
import { fieldCls } from '@/lib/form-styles';
import { canWrite } from '@/lib/access';

function RunSummary({ run }: { run: import('@/lib/api').SchedulerRun }): JSX.Element {
  const summaries = run.metadata.summaries ?? [];
  const total = summaries.reduce(
    (acc, s) => ({
      attempted: acc.attempted + s.attempted,
      succeeded: acc.succeeded + s.succeeded,
      failed: acc.failed + s.failed,
      errored: acc.errored + s.errored,
    }),
    { attempted: 0, succeeded: 0, failed: 0, errored: 0 },
  );
  const healthColor =
    total.errored > 0
      ? 'text-status-error'
      : total.failed > total.succeeded
        ? 'text-status-warning'
        : 'text-status-success';
  return (
    <li className="py-3 text-sm flex flex-wrap gap-x-4 items-center">
      <span className="text-ink-muted tabular-nums min-w-[12rem]">
        {new Date(run.occurred_at).toLocaleString()}
      </span>
      <span className={`font-medium ${healthColor}`}>
        {total.succeeded}/{total.attempted} ok
      </span>
      {total.failed > 0 ? (
        <span className="text-status-warning">· {total.failed} fallidos</span>
      ) : null}
      {total.errored > 0 ? (
        <span className="text-status-error">· {total.errored} errored</span>
      ) : null}
      <span className="text-xs text-ink-muted font-mono">
        {summaries.map((s) => s.provider).join(', ')}
      </span>
    </li>
  );
}

function QueueRow({
  queue,
  onRedrive,
}: {
  queue: QueueInfo;
  onRedrive: (name: string) => void;
}): JSX.Element {
  const session = useSession();
  const canRedrive = canWrite(session, 'jobs');
  const dlqHigh = (queue.dlq_approximate_messages ?? 0) > 0;

  return (
    <div className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border p-4 space-y-3">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-medium">{queue.name}</h3>
          <p className="text-xs text-ink-muted">{queue.description}</p>
        </div>
        {queue.available ? (
          <span className="text-xs text-status-success">available</span>
        ) : (
          <span className="text-xs text-status-error" title={queue.error_message ?? ''}>
            unavailable
          </span>
        )}
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div>
          <p className="text-xs text-ink-muted">visible</p>
          <p className="tabular-nums font-semibold">{queue.approximate_messages}</p>
        </div>
        <div>
          <p className="text-xs text-ink-muted">in-flight</p>
          <p className="tabular-nums">{queue.approximate_in_flight}</p>
        </div>
        <div>
          <p className="text-xs text-ink-muted">delayed</p>
          <p className="tabular-nums">{queue.approximate_delayed}</p>
        </div>
        <div>
          <p className="text-xs text-ink-muted">DLQ</p>
          <p className={`tabular-nums font-semibold ${dlqHigh ? 'text-status-error' : ''}`}>
            {queue.dlq_approximate_messages ?? '—'}
          </p>
        </div>
      </div>

      {queue.has_dlq && queue.dlq_name ? (
        <div className="flex items-center justify-between pt-2 border-t border-surface-border dark:border-surface-dark-border">
          <span className="text-xs text-ink-muted font-mono">DLQ: {queue.dlq_name}</span>
          <button
            type="button"
            data-help="jobs.redrive"
            onClick={() => onRedrive(queue.name)}
            disabled={!canRedrive || !dlqHigh}
            title={canRedrive ? '' : 'Solo rol admin puede hacer redrive'}
            className="text-sm rounded-lg bg-status-warning/20 text-status-warning hover:bg-status-warning/30 px-3 py-1.5 focus-visible:ring-2 focus-visible:ring-status-warning disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Redrive DLQ
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Cobros frenados por el procesador.
 *
 * Es la pantalla que le faltaba al incidente de agosto-septiembre de 2026: 22
 * suscripciones estuvieron un mes sin cobrarse y no habia ningun lugar del
 * backoffice donde se viera. Se veia solamente en CloudWatch, y solo si uno
 * sabia que buscar.
 *
 * Deliberadamente NO muestra un boton cuando no hay nada frenado: el estado
 * sano de esta seccion es no existir.
 */
function ChargeErrors({
  groups,
  onRetry,
}: {
  groups: ChargeErrorGroup[];
  onRetry: () => void;
}): JSX.Element | null {
  const session = useSession();
  const canRetry = canWrite(session, 'jobs');
  if (groups.length === 0) return null;

  const total = groups.reduce((acc, g) => acc + g.subscriptions, 0);

  return (
    <section
      data-help="jobs.charge_errors"
      aria-labelledby="charge-errors-title"
      className="bg-surface dark:bg-surface-dark rounded-xl border border-status-error/40 overflow-hidden"
    >
      <header className="px-5 py-3 border-b border-surface-border dark:border-surface-dark-border flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 id="charge-errors-title" className="text-sm font-medium text-status-error">
            {total} {total === 1 ? 'suscripción no se está cobrando' : 'suscripciones no se están cobrando'}
          </h2>
          <p className="text-xs text-ink-muted">
            El medio de pago no llegó a preguntarle al banco. No son tarjetas rechazadas.{' '}
            <Link to="/cobros" className="text-brand hover:underline">
              Verlas una por una en Requiere atención
            </Link>
          </p>
        </div>
        <button
          type="button"
          data-help="jobs.charge_retry"
          onClick={onRetry}
          disabled={!canRetry}
          title={canRetry ? '' : 'Requiere Jobs & DLQ en nivel completo'}
          className="text-sm rounded-lg bg-status-error/20 text-status-error hover:bg-status-error/30 px-3 py-1.5 focus-visible:ring-2 focus-visible:ring-status-error disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Reintentar ahora
        </button>
      </header>
      <ul className="divide-y divide-surface-border dark:divide-surface-dark-border">
        {groups.map((g) => (
          <li key={`${g.partner_id}:${g.error_code}`} className="px-5 py-3 text-sm space-y-1">
            <div className="flex flex-wrap gap-x-3 items-baseline">
              <span className="font-medium">{g.partner_id}</span>
              <span className="tabular-nums text-status-error font-semibold">
                {g.subscriptions}
              </span>
              <span className="font-mono text-xs text-ink-muted">{g.error_code}</span>
              {g.oldest_error_at ? (
                <span className="text-xs text-ink-muted">
                  desde {new Date(g.oldest_error_at).toLocaleString()}
                </span>
              ) : null}
            </div>
            {g.sample_message ? (
              <p className="text-xs text-ink-muted font-mono break-words">{g.sample_message}</p>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function Jobs(): JSX.Element {
  const qc = useQueryClient();
  const [redriveTarget, setRedriveTarget] = useState<string | null>(null);
  const [maxPerSecond, setMaxPerSecond] = useState('10');
  const [retryOpen, setRetryOpen] = useState(false);

  const jobsQ = useQuery({
    queryKey: ['admin-jobs-status'],
    queryFn: fetchJobsStatus,
    refetchInterval: 10_000,
  });

  const retryMut = useMutation({
    mutationFn: () => retryChargeErrors(),
    onSuccess: () => {
      setRetryOpen(false);
      qc.invalidateQueries({ queryKey: ['admin-jobs-status'] });
    },
  });

  const redriveMut = useMutation({
    mutationFn: (queueName: string) =>
      redriveDlq(queueName, maxPerSecond ? parseInt(maxPerSecond, 10) : undefined),
    onSuccess: () => {
      setRedriveTarget(null);
      qc.invalidateQueries({ queryKey: ['admin-jobs-status'] });
    },
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 data-help-page="jobs.page" data-help="jobs.page" className="text-2xl font-semibold">Jobs & DLQ</h1>
        <p className="text-sm text-ink-muted">
          Últimos runs del scheduler + estado de queues (refresh cada 10s).
        </p>
      </header>

      <section
        data-help="jobs.scheduler"
        aria-labelledby="scheduler-title"
        className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border"
      >
        <header className="px-5 py-3 border-b border-surface-border dark:border-surface-dark-border">
          <h2 id="scheduler-title" className="text-sm font-medium">
            subscription-scheduler — últimos runs
          </h2>
        </header>
        <div className="p-5">
          {jobsQ.data?.scheduler_runs.length === 0 ? (
            <p className="text-sm text-ink-muted">
              Sin runs registrados. El cron corre cada 15 minutos y escribe en audit_log.
            </p>
          ) : null}
          {jobsQ.data?.scheduler_runs ? (
            <ul className="divide-y divide-surface-border dark:divide-surface-dark-border">
              {jobsQ.data.scheduler_runs.map((r) => (
                <RunSummary key={r.audit_id} run={r} />
              ))}
            </ul>
          ) : null}
        </div>
      </section>

      <ChargeErrors
        groups={jobsQ.data?.charge_errors ?? []}
        onRetry={() => setRetryOpen(true)}
      />

      <section aria-labelledby="queues-title" data-help="jobs.queues">
        <h2 id="queues-title" className="text-sm font-medium mb-3 text-ink-muted uppercase tracking-wider">
          Queues del sistema
        </h2>
        <div className="space-y-3">
          {jobsQ.data?.queues.map((q) => (
            <QueueRow key={q.name} queue={q} onRedrive={setRedriveTarget} />
          ))}
        </div>
      </section>

      <ConfirmDialog
        open={retryOpen}
        title="Reintentar los cobros frenados"
        description={
          <div className="space-y-2">
            <p>
              Vas a liberar el freno de los cobros que quedaron trabados por un error del
              medio de pago. La próxima corrida del cobrador, dentro de 15 minutos como
              máximo, los vuelve a intentar.
            </p>
            <p className="text-xs">
              Hacelo <strong className="text-ink dark:text-ink-inverted">después</strong> de
              corregir la causa. Si el problema sigue, los cobros vuelven a fallar y el freno
              se aplica de nuevo.
            </p>
            <p className="text-xs">
              No toca las suscripciones en período de gracia por una tarjeta rechazada: esas
              tienen su fecha de reintento acordada.
            </p>
            {retryMut.isError ? (
              <p className="text-status-error text-xs">
                Error:{' '}
                {retryMut.error instanceof AdminApiError
                  ? retryMut.error.message
                  : 'desconocido'}
              </p>
            ) : null}
          </div>
        }
        confirmLabel="Reintentar ahora"
        loading={retryMut.isPending}
        onConfirm={() => retryMut.mutate()}
        onCancel={() => {
          setRetryOpen(false);
          retryMut.reset();
        }}
      />

      <ConfirmDialog
        open={redriveTarget !== null}
        title="Redrive DLQ"
        description={
          <div className="space-y-2">
            <p>
              Vas a mover los mensajes de la DLQ de{' '}
              <strong className="text-ink dark:text-ink-inverted">{redriveTarget}</strong> de vuelta a la queue original.
            </p>
            <p className="text-xs">
              Esto puede disparar re-ejecución de webhooks. El partner debe ser idempotente
              (ver <code className="font-mono">event_id</code>).
            </p>
            <label className="block text-sm pt-2">
              <span className="font-medium">Max mensajes/segundo:</span>
              <input
                type="number"
                min={1}
                max={500}
                value={maxPerSecond}
                onChange={(e) => setMaxPerSecond(e.target.value)}
                className={fieldCls({ block: false, extra: 'ml-2 w-20' })}
              />
            </label>
            {redriveMut.isError ? (
              <p className="text-status-error text-xs">
                Error:{' '}
                {redriveMut.error instanceof AdminApiError
                  ? redriveMut.error.message
                  : 'desconocido'}
              </p>
            ) : null}
          </div>
        }
        confirmLabel="Redrive"
        danger
        loading={redriveMut.isPending}
        onConfirm={() => redriveTarget && redriveMut.mutate(redriveTarget)}
        onCancel={() => {
          setRedriveTarget(null);
          redriveMut.reset();
        }}
      />
    </div>
  );
}
